import { useRef, useState, useCallback } from 'react';
import { getIceServers } from '../iceServers';

export function useWebRTC(socket, sessionCode, role) {
  const peerRef = useRef(null);
  const localStreamRef = useRef(null);
  const [sharing, setSharing] = useState(false);
  const [error, setError] = useState(null);

  const createPeer = useCallback(async () => {
    if (peerRef.current) peerRef.current.close();
    peerRef.current = null;

    const peer = new RTCPeerConnection({ iceServers: await getIceServers(socket) });

    peer.onicecandidate = (e) => {
      if (e.candidate && socket) {
        console.log('[Klijent] ICE kandidat šalje se serveru');
        socket.emit('webrtc:ice', { code: sessionCode, candidate: e.candidate });
      }
    };

    peer.oniceconnectionstatechange = () => {
      console.log('[Klijent] ICE state:', peer.iceConnectionState);
    };

    peer.onconnectionstatechange = () => {
      console.log('[Klijent] Connection state:', peer.connectionState);
    };

    peerRef.current = peer;
    return peer;
  }, [socket, sessionCode]);

  const startSharing = useCallback(async () => {
    try {
      console.log('[Klijent] startSharing pozvan, sessionCode:', sessionCode);
      setError(null);

      // Ponudi ceo ekran kao podrazumevani izbor (inace korisnici cesto izaberu
      // samo karticu browsera, pa agent ne vidi nista van nje)
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: 'monitor', frameRate: 15, width: { ideal: 1280 } },
        audio: false,
        selfBrowserSurface: 'exclude',
        monitorTypeSurfaces: 'include',
        surfaceSwitching: 'include'
      });
      const surface = stream.getVideoTracks()[0].getSettings().displaySurface;
      if (surface && surface !== 'monitor') {
        console.warn('[Klijent] Deli se samo', surface, '- agent nece videti ostatak ekrana');
      }
      console.log('[Klijent] Stream dobijen:', stream.getVideoTracks()[0].label);

      localStreamRef.current = stream;
      const peer = await createPeer();
      console.log('[Klijent] Peer kreiran');

      stream.getTracks().forEach(track => {
        peer.addTrack(track, stream);
        console.log('[Klijent] Track dodat:', track.kind, track.readyState);
      });

      stream.getVideoTracks()[0].onended = () => {
        console.log('[Klijent] Korisnik zaustavio deljenje');
        stopSharing();
      };

      const offer = await peer.createOffer();
      await peer.setLocalDescription(offer);
      console.log('[Klijent] Offer kreiran i postavljen');

      socket.emit('client:sharing', { code: sessionCode });
      setTimeout(() => {
        socket.emit('webrtc:offer', { code: sessionCode, offer });
        console.log('[Klijent] Offer poslat serveru');
      }, 500);

      setSharing(true);

    } catch (err) {
      console.error('[Klijent] Greška:', err);
      if (err.name === 'NotAllowedError') {
        setError('Niste dozvolili deljenje ekrana.');
      } else {
        setError('Greška pri deljenju ekrana: ' + err.message);
      }
    }
  }, [socket, sessionCode, createPeer]);

  const stopSharing = useCallback(() => {
    console.log('[Klijent] stopSharing pozvan');
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }
    if (peerRef.current) {
      peerRef.current.close();
      peerRef.current = null;
    }
    if (socket) socket.emit('client:stopped', { code: sessionCode });
    setSharing(false);
  }, [socket, sessionCode]);

  const handleAnswer = useCallback(async (answer) => {
    console.log('[Klijent] Answer primljen');
    if (!peerRef.current) return;
    await peerRef.current.setRemoteDescription(new RTCSessionDescription(answer));
    console.log('[Klijent] Remote description postavljena');
  }, []);

  const handleIce = useCallback(async (candidate) => {
    if (!peerRef.current) return;
    try {
      await peerRef.current.addIceCandidate(new RTCIceCandidate(candidate));
      console.log('[Klijent] ICE kandidat dodat');
    } catch (e) {
      console.warn('[Klijent] ICE greška:', e);
    }
  }, []);

  return {
    sharing,
    error,
    startSharing,
    stopSharing,
    handleAnswer,
    handleIce
  };
}
